import { useRef, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, Download, AlertTriangle, CheckCircle2, Sparkles, X, SkipForward } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

/** Bulk upload trigger with AI Duplicate Resolution verification popup. */
export default function BulkUploadButton({ direction = "inward", onDone, testid }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dupInfo, setDupInfo] = useState(null);  // result of "analyze" mode
  const [pickedFile, setPickedFile] = useState(null);
  const fileRef = useRef(null);

  const post = async (file, decision) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("direction", direction);
    fd.append("decision", decision);
    const { data } = await api.post("/parcels/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const startUpload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null); setDupInfo(null); setPickedFile(file);
    try {
      // Phase 1 · ANALYZE — no DB writes; server returns duplicate groups.
      const analysis = await post(file, "analyze");
      if ((analysis.duplicate_groups_found || 0) > 0) {
        setDupInfo(analysis);            // prompt user; DO NOT auto-import
      } else {
        // No duplicates → import straight away (safe path with decision=skip).
        const data = await post(file, "skip");
        setResult(data);
        toast.success(`Imported ${data.created.length} parcels`);
        onDone && onDone();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const commit = async (decision) => {
    if (!pickedFile) return;
    setBusy(true);
    try {
      const data = await post(pickedFile, decision);
      setDupInfo(null);
      if (decision === "cancel") {
        toast.info("Upload cancelled. Correct the source file and re-upload.");
        setResult({ cancelled: true, created: [], errors: [], total_rows: 0 });
      } else {
        setResult(data);
        toast.success(
          decision === "consolidate"
            ? `AI consolidated · ${data.created.length} parcels imported (${data.duplicates_skipped || 0} rows merged)`
            : `Imported ${data.created.length} parcels · skipped ${data.duplicates_skipped || 0} duplicates`
        );
        onDone && onDone();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const template = () => {
    const cols = ["tracking_number","awb_number","courier","shipping_mode","item_count","weight_kg","parcel_type","sender_name","sender_phone","sender_address","recipient_name","recipient_department","recipient_phone","recipient_address","mail_type","rto"];
    if (direction === "outward") cols.push("source_pincode","destination_city","destination_pincode");
    const rows = [cols.join(",")];
    if (direction === "inward") {
      rows.push([",,BlueDart,,1,0.5,Document,Amazon,9999900001,Bangalore,Priya Sharma,Finance,9945010001,HQ Floor 2,Official,No"].join(""));
      rows.push([",,Delhivery,,1,1.2,Package,Vendor Co,9820010001,,Ravi Kumar,Ops,9945010002,,Personal,Yes"].join(""));
    } else {
      rows.push([",,Delhivery,Surface,1,1.2,Package,Ravi Kumar,9820010001,HQ Mailroom,Global Solutions,,,,Official,No,560001,Chennai,600001"].join(""));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `bulk-${direction}-template.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const downloadVerificationReport = () => {
    if (!dupInfo) return;
    const lines = [
      `AI Bulk Upload Verification Report`,
      `Direction: ${direction}`,
      `Total rows: ${dupInfo.total_rows}`,
      `Duplicate groups found: ${dupInfo.duplicate_groups_found}`,
      `Affected records: ${dupInfo.affected_records}`,
      ``,
      `Group,RowNumbers,Sender,Recipient,City,PIN,Courier,Type,Weight`,
      ...dupInfo.duplicate_groups.map((g, i) => {
        const s = g.sample || {};
        return [
          `Group_${i+1}`, `"${g.row_numbers.join(' ')}"`, s.sender_name || s.sender || "",
          s.recipient_name || s.recipient || "", s.destination_city || "",
          s.destination_pincode || "", s.courier || "", s.parcel_type || "", s.weight_kg || "",
        ].join(",");
      }),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `bulk-verification-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <>
      <Button data-testid={testid || `bulk-${direction}-btn`}
              onClick={() => { setOpen(true); setResult(null); setDupInfo(null); setPickedFile(null); }}
              variant="outline" className="rounded-md">
        <FileSpreadsheet size={14} className="mr-2" /> Bulk upload
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Bulk {direction} upload</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm border border-border rounded-md p-3 bg-muted/40">
              Upload a <b>CSV</b> or <b>.xlsx</b> file. First row must be column headers.
              Required columns: <span className="mono">sender_name, recipient_name</span>.
            </div>
            <Button data-testid="bulk-template" variant="outline" onClick={template} className="rounded-md w-full">
              <Download size={14} className="mr-2" /> Download CSV template
            </Button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" hidden data-testid="bulk-file"
                   onChange={(e) => startUpload(e.target.files?.[0])} />
            <Button data-testid="bulk-pick" onClick={() => fileRef.current?.click()} disabled={busy}
                    className="rounded-md bg-primary text-primary-foreground shadow-brut-orange w-full">
              <Upload size={14} className="mr-2" /> {busy ? "Analysing…" : "Choose file"}
            </Button>

            {result && (
              <div className="border border-border rounded-md p-3 space-y-2" data-testid="bulk-result">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 size={14} className="text-primary" />
                  {result.cancelled
                    ? "Upload cancelled. No records were imported."
                    : `Imported ${result.created?.length ?? 0} / ${result.total_rows ?? 0} rows`}
                </div>
                {(result.duplicates_skipped || 0) > 0 && (
                  <div className="text-[11px] text-muted-foreground">
                    Duplicates skipped: <b>{result.duplicates_skipped}</b>
                    {result.duplicate_decision && ` · decision: ${result.duplicate_decision}`}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Bulk Upload Verification popup */}
      <Dialog open={!!dupInfo} onOpenChange={(v) => !v && setDupInfo(null)}>
        <DialogContent className="rounded-md max-w-lg" data-testid="dup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles size={16} className="text-primary" /> AI Bulk Upload Verification
            </DialogTitle>
          </DialogHeader>
          {dupInfo && (
            <div className="space-y-3 text-sm">
              <div className="border border-amber-300 bg-amber-50 rounded-md p-3">
                <div className="font-semibold text-amber-800 flex items-center gap-2">
                  <AlertTriangle size={13} /> Potential duplicate records detected.
                </div>
                <div className="mt-1 grid grid-cols-2 gap-1 text-[11px]">
                  <div>Duplicate groups found: <b className="mono">{dupInfo.duplicate_groups_found}</b></div>
                  <div>Affected records: <b className="mono">{dupInfo.affected_records}</b></div>
                </div>
              </div>
              <div className="text-[12px] text-muted-foreground">{dupInfo.message}</div>
              <div className="border border-border rounded-md max-h-40 overflow-y-auto text-[11px]">
                {dupInfo.duplicate_groups.slice(0, 10).map((g, i) => (
                  <div key={i} className="p-2 border-b border-border last:border-0">
                    <div className="mono text-[10px] text-muted-foreground">
                      Group {i+1} · rows {g.row_numbers.join(", ")} ({g.count} similar)
                    </div>
                    <div className="mt-0.5">
                      {g.sample?.sender_name || g.sample?.sender} → {g.sample?.recipient_name || g.sample?.recipient}
                      {g.sample?.destination_city ? ` · ${g.sample.destination_city}` : ""}
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <Button data-testid="dup-consolidate" disabled={busy}
                        onClick={() => commit("consolidate")}
                        className="rounded-md bg-primary text-primary-foreground">
                  <Sparkles size={13} className="mr-1" /> Proceed with AI Consolidation
                </Button>
                <Button data-testid="dup-skip" variant="outline" disabled={busy}
                        onClick={() => commit("skip")}
                        className="rounded-md">
                  <SkipForward size={13} className="mr-1" /> Skip Duplicate Records &amp; Continue Import
                </Button>
                <Button data-testid="dup-cancel" variant="outline" disabled={busy}
                        onClick={() => { downloadVerificationReport(); commit("cancel"); }}
                        className="rounded-md text-destructive">
                  <X size={13} className="mr-1" /> Cancel Upload &amp; Download Verification Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
